import { API_BASE_URL } from "../config";
import React, { useState, useContext, useEffect } from "react";
import axios from "axios";
import GeneralContext from "./GeneralContext";
import { AuthContext } from "../context/AuthContext";
import { MarketContext } from "../context/MarketContext";
import "./BuyActionWindow.css";

const BuyActionWindow = ({ uid, mode = "BUY" }) => {
    const [stockQuantity, setStockQuantity] = useState(1);
    const [stockPrice, setStockPrice] = useState(100.0);
    const [triggerPrice, setTriggerPrice] = useState(0.0);
    const [orderType, setOrderType] = useState("MARKET");
    const [productType, setProductType] = useState("CNC");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const { closeBuyWindow, closeSellWindow } = useContext(GeneralContext);
    const { refreshWallet, wallet } = useContext(AuthContext);
    const { getLtp } = useContext(MarketContext);

    // Auto-fill live LTP when order window opens
    useEffect(() => {
        if (uid) {
            const livePrice = getLtp(uid, 100.0);
            setStockPrice(Number(livePrice.toFixed(2)));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uid]);

    const isBuy = mode.toUpperCase() === "BUY";
    
    // Indicative margin calculation (flat percentage)
    const notionalValue = (Number(stockQuantity) || 0) * (Number(stockPrice) || 0);
    const marginPct = productType === "MIS" ? 20 : 100; // 20% for MIS, 100% for CNC
    const marginRequired = (notionalValue * marginPct) / 100.0;

    const handleActionClick = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        try {
            await axios.post(`${API_BASE_URL}/api/client/newOrder`, {
                name: uid,
                qty: Number(stockQuantity),
                price: Number(stockPrice),
                mode: isBuy ? "BUY" : "SELL",
                productType: productType,
                orderType: orderType,
                triggerPrice: Number(triggerPrice)
            });

            await refreshWallet();

            if (isBuy) {
                closeBuyWindow();
            } else {
                closeSellWindow();
            }
        } catch (err) {
            setError(err.response?.data?.message || "Order execution failed.");
        } finally {
            setLoading(false);
        }
    };

    const handleCancelClick = (e) => {
        e.preventDefault();
        if (isBuy) {
            closeBuyWindow();
        } else {
            closeSellWindow();
        }
    };

    return (
        <div className="container" id="buy-window" style={{ borderTop: `4px solid ${isBuy ? "#4184f3" : "#ef5350"}`, width: "360px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                <span style={{ fontWeight: "bold", fontSize: "15px", color: isBuy ? "#4184f3" : "#ef5350" }}>
                    {isBuy ? "BUY" : "SELL"} {uid} (Virtual OMS)
                </span>
                <span style={{ fontSize: "11px", backgroundColor: "#eef6ff", color: "#1976d2", padding: "2px 6px", borderRadius: "4px" }}>
                    {productType}
                </span>
            </div>

            <div style={{ fontSize: "11px", color: "#888", marginBottom: "10px" }}>
                ℹ️ <em>Indicative margin — not exact broker SPAN margin</em>
            </div>

            {error && <div style={{ color: "#c62828", fontSize: "12px", marginBottom: "8px", backgroundColor: "#ffebee", padding: "6px", borderRadius: "4px" }}>{error}</div>}

            {/* Order & Product Selectors */}
            <div style={{ display: "flex", gap: "10px", marginBottom: "12px" }}>
                <div style={{ flex: 1 }}>
                    <label style={{ fontSize: "11px", color: "#666" }}>Product</label>
                    <select value={productType} onChange={(e) => setProductType(e.target.value)} style={{ width: "100%", padding: "6px", fontSize: "12px" }}>
                        <option value="CNC">CNC (Delivery)</option>
                        <option value="MIS">MIS (Intraday - 5x)</option>
                    </select>
                </div>
                <div style={{ flex: 1 }}>
                    <label style={{ fontSize: "11px", color: "#666" }}>Type</label>
                    <select value={orderType} onChange={(e) => setOrderType(e.target.value)} style={{ width: "100%", padding: "6px", fontSize: "12px" }}>
                        <option value="MARKET">MARKET</option>
                        <option value="LIMIT">LIMIT</option>
                        <option value="SL">SL</option>
                        <option value="SL-M">SL-M</option>
                    </select>
                </div>
            </div>

            <div className="regular-order">
                <div className="inputs" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                    <fieldset style={{ margin: 0 }}>
                        <legend style={{ fontSize: "11px" }}>Qty</legend>
                        <input
                            type="number"
                            min="1"
                            onChange={(e) => setStockQuantity(e.target.value)}
                            value={stockQuantity}
                            style={{ width: "100%", boxSizing: "border-box" }}
                        />
                    </fieldset>
                    <fieldset style={{ margin: 0 }}>
                        <legend style={{ fontSize: "11px" }}>Price (₹)</legend>
                        <input
                            type="number"
                            step="0.05"
                            disabled={orderType === "MARKET" || orderType === "SL-M"}
                            onChange={(e) => setStockPrice(e.target.value)}
                            value={stockPrice}
                            style={{ width: "100%", boxSizing: "border-box" }}
                        />
                    </fieldset>
                </div>
            </div>

            {(orderType === "SL" || orderType === "SL-M") && (
                <div style={{ marginTop: "8px" }}>
                    <label style={{ fontSize: "11px", color: "#666" }}>Trigger Price (₹)</label>
                    <input
                        type="number"
                        step="0.05"
                        value={triggerPrice}
                        onChange={(e) => setTriggerPrice(e.target.value)}
                        style={{ width: "100%", padding: "6px", boxSizing: "border-box" }}
                    />
                </div>
            )}

            <div className="buttons" style={{ marginTop: "14px" }}>
                <div style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontSize: "12px", color: "#333" }}>Margin Required: <strong style={{ color: "#2e7d32" }}>₹{marginRequired.toFixed(2)}</strong></span>
                    {wallet && <span style={{ fontSize: "11px", color: "#888" }}>Avail: ₹{wallet.marginAvailable?.toLocaleString('en-IN')}</span>}
                </div>
                <div>
                    <button
                        onClick={handleActionClick}
                        disabled={loading}
                        className={`btn ${isBuy ? "btn-blue" : "btn-red"}`}
                        style={{ border: "none", padding: "8px 14px", cursor: "pointer", fontWeight: "bold", borderRadius: "4px" }}
                    >
                        {loading ? "Filling..." : isBuy ? "Place Buy" : "Place Sell"}
                    </button>
                    <button
                        onClick={handleCancelClick}
                        className="btn btn-grey"
                        style={{ border: "none", padding: "8px 12px", cursor: "pointer", borderRadius: "4px", marginLeft: "6px" }}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BuyActionWindow;